import type {
    TResponseGetOverlappingPeriods,
    TResponseGetStandardisedExtraction,
    TStandardizedCustomPeriodBudgetColumn,
    TStandardizedCustomPeriodTrailingColumn,
    TStandardizedRow,
    TStandardizedMonthColumn,
    TStandardizedYearColumn,
    TStandardizedTotalColumn,
    TStandardizedTrailingYearToDateColumn,
    TStandardizedBudgetYearToDateColumn,
} from "@/apis/odin/apiODIN";

import type {
    TDtoStandardizedTableExtraction,
    TActiveColumnChange,
} from "./TDtoStandardizedTableExtraction";
import { formatOperatingStatementLabel } from "./formatUtils";

type StandardizedColumn =
    | TStandardizedMonthColumn
    | TStandardizedYearColumn
    | TStandardizedTotalColumn
    | TStandardizedCustomPeriodTrailingColumn
    | TStandardizedCustomPeriodBudgetColumn;

const getFieldIdForColumn = (
    columnType: string | null | undefined,
    columnIndex: number,
): string => {
    switch (columnType) {
        case "total":
            return "operating-statement-total-column";
        case "trailing_year_to_date":
            return `operating-statement-trailing-year-to-date-column-${columnIndex}`;
        case "budget_year_to_date":
            return `operating-statement-period-to-date-column-${columnIndex}`;
        case "custom_period_trailing":
            return `operating-statement-custom-period-trailing-${columnIndex}`;
        case "custom_period_budget":
            return `operating-statement-custom-period-budget-${columnIndex}`;
        case "actual_year":
        case "budget_year":
            return `operating-statement-year-id-${columnIndex}`;
        default:
            return `operating-statement-month-id-${columnIndex}`;
    }
};

const isStandardizedMonthColumn = (
    column: StandardizedColumn,
): column is TStandardizedMonthColumn => {
    return (
        column.columnType === "budget_month" ||
        column.columnType === "actual_month"
    );
};

const isStandardizedYearColumn = (
    column: StandardizedColumn,
): column is TStandardizedYearColumn => {
    return (
        column.columnType === "actual_year" ||
        column.columnType === "budget_year"
    );
};

const isStandardizedTotalColumn = (
    column: StandardizedColumn,
): column is TStandardizedTotalColumn => {
    return column.columnType === "total";
};

const isStandardizedTrailingYearToDateColumn = (
    column: StandardizedColumn,
): column is TStandardizedTrailingYearToDateColumn => {
    return column.columnType === "trailing_year_to_date";
};

const isStandardizedBudgetYearToDateColumn = (
    column: StandardizedColumn,
): column is TStandardizedBudgetYearToDateColumn => {
    return column.columnType === "budget_year_to_date";
};

const isStandardizedCustomPeriodTrailingColumn = (
    column: StandardizedColumn,
): column is TStandardizedCustomPeriodTrailingColumn => {
    return column.columnType === "custom_period_trailing";
};

const isStandardizedCustomPeriodBudgetColumn = (
    column: StandardizedColumn,
): column is TStandardizedCustomPeriodBudgetColumn => {
    return column.columnType === "custom_period_budget";
};

type TOverlappingPeriods = {
    label: string;
    active: boolean;
    extractionId: number;
    cells: TResponseGetStandardisedExtraction["rowCells"];
    rows: TResponseGetStandardisedExtraction["rows"];
    duplication: boolean;
    columnIndex?: number;
    periodId: number;
    total: string;
    periodForCheck?: PeriodForCheck;
};

type OperatingStatementColumn = {
    label: string;
    columnIndex: number;
    columnType: string;
    month?: number;
    year?: number;
};

type PeriodForCheck = {
    startMonth: { month: number; year: number };
    endMonth: { month: number; year: number };
    columnType: string;
};

type OperatingStatementColumnWithPeriod = OperatingStatementColumn & {
    periodForCheck?: PeriodForCheck;
};

const comparePeriods = (first: PeriodForCheck, second: PeriodForCheck) => {
    return (
        first.startMonth.month === second.startMonth.month &&
        first.startMonth.year === second.startMonth.year &&
        first.endMonth.month === second.endMonth.month &&
        first.endMonth.year === second.endMonth.year &&
        first.columnType === second.columnType
    );
};

type StandardizedPeriodColumnWithoutColumnOrder = Omit<
    TStandardizedMonthColumn | TStandardizedYearColumn,
    "columnOrder"
>;

const columnLabel = (
    column: StandardizedPeriodColumnWithoutColumnOrder,
    isAtB: boolean,
) => {
    if (
        column.columnType === "budget_month" ||
        column.columnType === "actual_month"
    ) {
        const dateLabel = formatOperatingStatementLabel(column.period[0]);
        const typeLabel =
            column.columnType === "budget_month" ? " Budget" : " Actual";
        return isAtB ? dateLabel + typeLabel : dateLabel;
    }
    if (
        column.columnType === "actual_year" ||
        column.columnType === "budget_year"
    ) {
        const dateLabel = `${column.period[0].year}`;
        const typeLabel =
            column.columnType === "actual_year" ? " Actual" : " Budget";
        return isAtB ? dateLabel + typeLabel : dateLabel;
    }
    if (
        column.columnType === "trailing_year_to_date" ||
        column.columnType === "budget_year_to_date"
    ) {
        const dateLabel = `YTD ${column.period[1].month}/${column.period[1].year}`;
        const typeLabel =
            column.columnType === "trailing_year_to_date"
                ? " Actual"
                : " Budget";
        return isAtB ? dateLabel + typeLabel : dateLabel;
    }
    if (
        column.columnType === "custom_period_trailing" ||
        column.columnType === "custom_period_budget"
    ) {
        const dateLabel = `PTD ${column.period[1].month}/${column.period[1].year}`;
        const typeLabel =
            column.columnType === "custom_period_trailing"
                ? " Actual"
                : " Budget";
        return isAtB ? dateLabel + typeLabel : dateLabel;
    }
    return column.label;
};

export const convertAPIResponseToDTOStandardizedTableExtraction = (
    apiResponse: TResponseGetStandardisedExtraction,
    overlappingPeriods: TResponseGetOverlappingPeriods,
): TDtoStandardizedTableExtraction => {
    const isDraft = apiResponse.status === "draft";
    const hasSubcategory = apiResponse.hasSubcategory;
    const activeColumns: Record<string, TActiveColumnChange> = {};
    const monthLabelToPeriodIdMap: Record<string, number> = {};
    let overlappingPeriodsColumns: TOverlappingPeriods[] = [];
    if (isDraft) {
        const isAtBExtraction =
            apiResponse.extractionType ===
            "operating_statement_actual_to_budget";
        overlappingPeriods.forEach(period => {
            const columnType =
                period.columnToCheck.columnType ?? "actual_month";
            const label = columnLabel(period.columnToCheck, isAtBExtraction);
            monthLabelToPeriodIdMap[label] = period.periodId;
            period.overlappingColumns.forEach(column => {
                overlappingPeriodsColumns = [
                    ...overlappingPeriodsColumns,
                    {
                        label,
                        extractionId: column.extractionId,
                        active: column.active,
                        cells: [column.cells],
                        rows: column.rows,
                        duplication: column.duplication,
                        periodId: period.periodId,
                        periodForCheck: {
                            startMonth: period.columnToCheck.period[0],
                            endMonth: period.columnToCheck.period[1],
                            columnType: columnType,
                        },
                        total: column.total,
                    },
                ];
            });
        });
    }
    const monthColumns = apiResponse.columns.filter(isStandardizedMonthColumn);
    const yearColumns = apiResponse.columns.filter(isStandardizedYearColumn);
    const operatingStatementTrailingYearToDateColumns =
        apiResponse.columns.filter(isStandardizedTrailingYearToDateColumn);
    const operatingStatementBudgetYearToDateColumns =
        apiResponse.columns.filter(isStandardizedBudgetYearToDateColumn);
    const operatingStatementCustomPeriodTrailingColumns =
        apiResponse.columns.filter(isStandardizedCustomPeriodTrailingColumn);
    const operatingStatementCustomPeriodBudgetColumns =
        apiResponse.columns.filter(isStandardizedCustomPeriodBudgetColumn);
    const lastColumnForTotal = apiResponse.columns.find(
        isStandardizedTotalColumn,
    );

    // Check if this is an Actual-to-Budget extraction
    const isAtB =
        apiResponse.extractionType === "operating_statement_actual_to_budget";

    const operatingStatementsMonthsColumnsWithTotal: OperatingStatementColumnWithPeriod[] =
        [
            ...monthColumns.map(
                (column): OperatingStatementColumnWithPeriod => {
                    const columnType = column.columnType ?? "actual_month";
                    return {
                        label: columnLabel(column, isAtB),
                        columnIndex: column.columnOrder,
                        columnType,
                        ...column.period[0],
                        periodForCheck: {
                            startMonth: {
                                month: column.period[0].month,
                                year: column.period[0].year,
                            },
                            endMonth: {
                                month: column.period[1].month,
                                year: column.period[1].year,
                            },
                            columnType: columnType,
                        },
                    };
                },
            ),
            ...yearColumns.map(
                (column): OperatingStatementColumnWithPeriod => ({
                    label: columnLabel(column, isAtB),
                    columnIndex: column.columnOrder,
                    columnType: column.columnType ?? "actual_year",
                    periodForCheck: {
                        startMonth: {
                            month: column.period[0].month,
                            year: column.period[0].year,
                        },
                        endMonth: {
                            month: column.period[1].month,
                            year: column.period[1].year,
                        },
                        columnType: column.columnType ?? "actual_year",
                    },
                }),
            ),
            ...operatingStatementTrailingYearToDateColumns.map(
                (column): OperatingStatementColumnWithPeriod => ({
                    label: columnLabel(column, isAtB),
                    columnIndex: column.columnOrder,
                    columnType: column.columnType ?? "trailing_year_to_date",
                    periodForCheck: {
                        startMonth: {
                            month: column.period[0].month,
                            year: column.period[0].year,
                        },
                        endMonth: {
                            month: column.period[1].month,
                            year: column.period[1].year,
                        },
                        columnType:
                            column.columnType ?? "trailing_year_to_date",
                    },
                }),
            ),
            ...operatingStatementBudgetYearToDateColumns.map(
                (column): OperatingStatementColumnWithPeriod => ({
                    label: columnLabel(column, isAtB),
                    columnIndex: column.columnOrder,
                    columnType: column.columnType ?? "budget_year_to_date",
                    periodForCheck: {
                        startMonth: {
                            month: column.period[0].month,
                            year: column.period[0].year,
                        },
                        endMonth: {
                            month: column.period[1].month,
                            year: column.period[1].year,
                        },
                        columnType: column.columnType ?? "budget_year_to_date",
                    },
                }),
            ),
            ...operatingStatementCustomPeriodTrailingColumns.map(
                (column): OperatingStatementColumnWithPeriod => ({
                    label: columnLabel(column, isAtB),
                    columnIndex: column.columnOrder,
                    columnType: column.columnType ?? "custom_period_trailing",
                    periodForCheck: {
                        startMonth: {
                            month: column.period[0].month,
                            year: column.period[0].year,
                        },
                        endMonth: {
                            month: column.period[1].month,
                            year: column.period[1].year,
                        },
                        columnType:
                            column.columnType ?? "custom_period_trailing",
                    },
                }),
            ),
            ...operatingStatementCustomPeriodBudgetColumns.map(
                (column): OperatingStatementColumnWithPeriod => ({
                    label: columnLabel(column, isAtB),
                    columnIndex: column.columnOrder,
                    columnType: column.columnType ?? "custom_period_budget",
                    periodForCheck: {
                        startMonth: {
                            month: column.period[0].month,
                            year: column.period[0].year,
                        },
                        endMonth: {
                            month: column.period[1].month,
                            year: column.period[1].year,
                        },
                        columnType: column.columnType ?? "custom_period_budget",
                    },
                }),
            ),
            ...(lastColumnForTotal
                ? [
                      {
                          label: lastColumnForTotal.label,
                          columnIndex: lastColumnForTotal.columnOrder,
                          columnType: lastColumnForTotal.columnType ?? "total",
                      },
                  ]
                : []),
        ];

    const columnDefsOperatingStatements =
        operatingStatementsMonthsColumnsWithTotal
            .toSorted((a, b) => {
                const getTypePriority = (columnType: string) => {
                    if (
                        columnType === "actual_month" ||
                        columnType === "budget_month"
                    )
                        return 0;
                    if (
                        columnType === "actual_year" ||
                        columnType === "budget_year"
                    )
                        return 1;
                    if (columnType === "custom_period_trailing") return 2;
                    if (columnType === "trailing_year_to_date") return 3;
                    if (columnType === "budget_year_to_date") return 4;
                    if (columnType === "custom_period_budget") return 5;
                    if (columnType === "total") return 6;
                    return 7;
                };
                const typePriorityDiff =
                    getTypePriority(a.columnType) -
                    getTypePriority(b.columnType);
                if (typePriorityDiff !== 0) {
                    return typePriorityDiff;
                }
                return a.columnIndex - b.columnIndex;
            })
            .map(({ label, columnIndex, columnType, periodForCheck }) => {
                const field = getFieldIdForColumn(columnType, columnIndex);
                if (columnIndex !== lastColumnForTotal?.columnOrder) {
                    activeColumns[label] = {
                        from: apiResponse.extractionId,
                        to: apiResponse.extractionId,
                    };
                }
                let children: Exclude<
                    TDtoStandardizedTableExtraction["columnDefsOperatingStatements"][number]["children"],
                    undefined
                > = [];
                let duplicatedExtractionId: number | undefined = undefined;

                if (isDraft) {
                    const matches = overlappingPeriodsColumns.filter(
                        c =>
                            c.periodForCheck !== null &&
                            c.periodForCheck !== undefined &&
                            periodForCheck !== null &&
                            periodForCheck !== undefined &&
                            comparePeriods(c.periodForCheck, periodForCheck),
                    );

                    if (matches.length > 0) {
                        matches.forEach(match => {
                            const currentField = `operating-statement-month-id-${columnIndex}-${match.extractionId}`;
                            const currentExtractionId = match.extractionId;
                            if (!match.duplication) {
                                children = [
                                    ...children,
                                    {
                                        headerName: label,
                                        field: currentField,
                                        extractionId: currentExtractionId,
                                    },
                                ];
                                match.columnIndex = columnIndex;
                                if (match.active) {
                                    activeColumns[label] = {
                                        from: currentExtractionId,
                                        to: apiResponse.extractionId,
                                    };
                                }
                            } else if (match.duplication) {
                                duplicatedExtractionId = currentExtractionId;
                            }
                        });
                    }
                }
                return {
                    headerName: label,
                    field,
                    children,
                    duplicatedExtractionId:
                        children.length === 0
                            ? duplicatedExtractionId
                            : undefined,
                    extractionId: apiResponse.extractionId,
                };
            });

    const reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues = (
        rowId: number,
    ): Record<string, string | number | null> =>
        operatingStatementsMonthsColumnsWithTotal.reduce(
            (acc, { columnIndex, columnType }) => {
                const currentMonthColumn = apiResponse.columns.find(
                    monthColumn => monthColumn.columnOrder === columnIndex,
                );
                const currentColumnCells = apiResponse.rowCells.find(
                    cell =>
                        cell["col_identifier"]?.toLowerCase() ===
                            currentMonthColumn?.label?.toLowerCase() ||
                        cell["col_identifier"]?.toLowerCase() ===
                            lastColumnForTotal?.label?.toLowerCase(),
                );
                const fieldId = getFieldIdForColumn(columnType, columnIndex);

                const fieldValue = currentColumnCells?.[rowId] ?? null;
                const hasValueThatCanBeCastedToNumber =
                    fieldValue !== null &&
                    !isNaN(Number(fieldValue)) &&
                    isFinite(Number(fieldValue));

                if (hasValueThatCanBeCastedToNumber) {
                    const fieldValueWithoutCommasAndDotsAndSpaces =
                        fieldValue.replace(/[.,\s]/g, "");
                    const fieldValueAsInteger = parseInt(
                        fieldValueWithoutCommasAndDotsAndSpaces,
                        10,
                    );
                    acc[fieldId] = fieldValueAsInteger;
                } else {
                    acc[fieldId] = fieldValue;
                }

                return acc;
            },
            {} as Record<string, string | number | null>,
        );

    const reduceOverlappingPeriodsToRows = (
        row: TStandardizedRow,
    ): {
        isEmpty: boolean;
        [key: string]: string | number | boolean | null;
    } =>
        overlappingPeriodsColumns.reduce(
            (acc, { columnIndex, extractionId, cells, rows }) => {
                const fieldKey = `operating-statement-month-id-${columnIndex}-${extractionId}`;
                const fieldRow = rows[row.rowOrder];
                const fieldValue = cells[0]?.[row.rowOrder] ?? null;
                const hasValueThatCanBeCastedToNumber =
                    fieldValue !== null &&
                    !isNaN(Number(fieldValue)) &&
                    isFinite(Number(fieldValue));
                if (hasValueThatCanBeCastedToNumber) {
                    const fieldValueWithoutCommasAndDotsAndSpaces =
                        fieldValue.replace(/[.,\s]/g, "");
                    const fieldValueAsInteger = parseInt(
                        fieldValueWithoutCommasAndDotsAndSpaces,
                        10,
                    );
                    acc[fieldKey] = fieldValueAsInteger;
                } else {
                    acc[fieldKey] = fieldValue;
                }
                acc["isEmpty"] = acc["isEmpty"] && (fieldRow?.isEmpty ?? true);
                return acc;
            },
            { isEmpty: row.isEmpty } as {
                isEmpty: boolean;
                [key: string]: string | number | boolean | null;
            },
        );

    const rowIdAccountMap: Record<number, string> = {};

    const buildStructuredRows = (
        sourceRows: typeof apiResponse.rows,
    ): TDtoStandardizedTableExtraction["rows"] => {
        let permanentRows: TDtoStandardizedTableExtraction["rows"] = [];
        let sectionBuffer: TDtoStandardizedTableExtraction["rows"] = [];
        let sectionName: string | null = null;
        let subcategoryRowId: number | null = null;

        const flush = () => {
            if (sectionName === null && sectionBuffer.length === 0) return;
            const sectionHeader: TDtoStandardizedTableExtraction["rows"] =
                sectionName !== null && sectionName.trim() !== ""
                    ? [
                          {
                              rowId: sectionName,
                              account: sectionName,
                              type: "HEADER",
                              isEmpty: false,
                          },
                      ]
                    : [];
            permanentRows = [
                ...permanentRows,
                ...sectionHeader,
                ...sectionBuffer,
            ];
            sectionBuffer = [];
            sectionName = null;
        };

        sourceRows.forEach(row => {
            if (row.rowType === "SECTION") {
                subcategoryRowId = null;
                sectionName = row.name;
                const sectionTotalRow: TDtoStandardizedTableExtraction["rows"] =
                    hasSubcategory && row.name.trim() !== ""
                        ? [
                              {
                                  rowId: row.rowOrder,
                                  account: `Total ${row.name}`,
                                  type: row.rowType,
                                  isEmpty: row.isEmpty,
                                  ...reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues(
                                      row.rowOrder,
                                  ),
                                  ...(isDraft
                                      ? reduceOverlappingPeriodsToRows(row)
                                      : {}),
                              },
                          ]
                        : [];
                sectionBuffer = [...sectionBuffer, ...sectionTotalRow];
                flush();
            } else if (row.rowType === "TYPE") {
                subcategoryRowId = null;
                flush();
                permanentRows = [
                    ...permanentRows,
                    {
                        rowId: row.rowOrder,
                        account: row.name,
                        type: row.rowType,
                        isEmpty: row.isEmpty,
                        ...reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues(
                            row.rowOrder,
                        ),
                        ...(isDraft ? reduceOverlappingPeriodsToRows(row) : {}),
                    },
                ];
            } else {
                const isNestedAccount =
                    row.rowType === "ACCOUNT" && row.depth === 1;
                if (row.rowType === "SUBSECTION") {
                    subcategoryRowId = row.rowOrder;
                } else if (!isNestedAccount) {
                    subcategoryRowId = null;
                }
                const parentRowIdField: Record<string, number | null> =
                    isNestedAccount ? { parentRowId: subcategoryRowId } : {};
                sectionBuffer = [
                    ...sectionBuffer,
                    {
                        rowId: row.rowOrder,
                        account: row.name,
                        type: row.rowType,
                        isEmpty: row.isEmpty,
                        ...parentRowIdField,
                        ...reduceOperatingStatementsMonthsColumnsWithTotalColumnToFieldValues(
                            row.rowOrder,
                        ),
                        ...(isDraft ? reduceOverlappingPeriodsToRows(row) : {}),
                    },
                ];
            }
        });
        flush();
        return permanentRows;
    };

    const operatingRows = apiResponse.rows.filter(row => !row.isNonOperating);
    operatingRows.forEach(row => {
        rowIdAccountMap[row.rowOrder] =
            row.rowType === "SECTION" ? `Total ${row.name}` : row.name;
    });

    const nonOperatingRows = buildStructuredRows(
        apiResponse.rows.filter(row => row.isNonOperating),
    );

    const rowsPermanentWithOperatingStatements =
        buildStructuredRows(operatingRows);
    const netIncomeTotalRowId = Object.keys(rowIdAccountMap).length;
    rowIdAccountMap[netIncomeTotalRowId] = "Net Income Total";
    const originalNetIncomeTotalRowId = Object.keys(rowIdAccountMap).length;
    rowIdAccountMap[originalNetIncomeTotalRowId] = "Original Net Income Total";
    let rowsDismissed: TDtoStandardizedTableExtraction["rowsDismissed"] = [];
    const nestedAccountRowOrders = new Set(
        apiResponse.rows
            .filter(row => row.rowType === "ACCOUNT" && row.depth === 1)
            .map(row => row.rowOrder),
    );
    const errorRows: TDtoStandardizedTableExtraction["errorRows"] =
        apiResponse.outliers.reduce(
            (acc, outlier) => {
                if (
                    outlier.rowOrder !== -1 &&
                    !nestedAccountRowOrders.has(outlier.rowOrder)
                ) {
                    const currentMonthColumn = apiResponse.columns.find(
                        m => m.label === outlier.columnLabel,
                    );
                    acc = [
                        ...acc,
                        {
                            rowId: outlier.rowOrder,
                            colId:
                                currentMonthColumn === undefined
                                    ? "operating-statement-total-column"
                                    : `operating-statement-month-id-${currentMonthColumn.columnOrder}`,
                            periodId: outlier.periodId,
                            columnName: (() => {
                                const matchByColumnOrder =
                                    operatingStatementsMonthsColumnsWithTotal.find(
                                        column =>
                                            column.columnIndex ===
                                            currentMonthColumn?.columnOrder,
                                    );
                                if (matchByColumnOrder) {
                                    return matchByColumnOrder.label;
                                }
                                const matchByLabel =
                                    operatingStatementsMonthsColumnsWithTotal.find(
                                        column =>
                                            column.label ===
                                            currentMonthColumn?.label,
                                    );
                                if (matchByLabel) {
                                    return matchByLabel.label;
                                }

                                if (
                                    currentMonthColumn &&
                                    isStandardizedMonthColumn(
                                        currentMonthColumn,
                                    )
                                ) {
                                    const matchByMonthYear =
                                        operatingStatementsMonthsColumnsWithTotal.find(
                                            column =>
                                                column.month !== undefined &&
                                                column.year !== undefined &&
                                                column.month ===
                                                    currentMonthColumn.period[0]
                                                        .month &&
                                                column.year ===
                                                    currentMonthColumn.period[0]
                                                        .year,
                                        );
                                    if (matchByMonthYear) {
                                        return matchByMonthYear.label;
                                    }
                                }

                                const matchTotalColumn =
                                    operatingStatementsMonthsColumnsWithTotal.find(
                                        column => column.month === undefined,
                                    );
                                return matchTotalColumn?.label;
                            })(),
                            accountName:
                                rowIdAccountMap[outlier.rowOrder] ?? "",
                            errorType: "dataOutlier",
                            errorValue: outlier.initialValue,
                            severity: "warning",
                            threshold: outlier.threshold,
                            direction:
                                outlier.category === "HIGH_VALUE"
                                    ? "large"
                                    : outlier.category === "LOW_VALUE"
                                      ? "small"
                                      : undefined,
                            dismissed:
                                outlier.dismissedAt !== null &&
                                outlier.dismissedAt !== undefined,
                        },
                    ];
                    if (
                        outlier.dismissedAt !== null &&
                        outlier.dismissedAt !== undefined
                    ) {
                        rowsDismissed = [
                            ...rowsDismissed,
                            {
                                rowId: outlier.rowOrder,
                                periodId: outlier.periodId,
                                accountLabel: outlier.accountLabel,
                            },
                        ];
                    }
                }
                return acc;
            },
            [] as TDtoStandardizedTableExtraction["errorRows"],
        );

    const formatTotalNetIncome = (
        data: Record<string, string>,
    ): Record<string, number> | null => {
        if (Object.keys(data).length === 0) {
            return null;
        }

        const months = monthColumns.map(month => {
            return {
                label: month.label,
                columnIndex: month.columnOrder,
            };
        });

        const years = yearColumns.map(year => {
            return {
                label: year.label,
                columnIndex: year.columnOrder,
            };
        });

        const trailingYearToDateCols =
            operatingStatementTrailingYearToDateColumns.map(column => {
                return {
                    label: column.label,
                    columnIndex: column.columnOrder,
                };
            });

        const budgetYearToDateCols =
            operatingStatementBudgetYearToDateColumns.map(column => {
                return {
                    label: column.label,
                    columnIndex: column.columnOrder,
                };
            });

        const customPeriodTrailingCols =
            operatingStatementCustomPeriodTrailingColumns.map(column => {
                return {
                    label: column.label,
                    columnIndex: column.columnOrder,
                };
            });

        const customPeriodBudgetCols =
            operatingStatementCustomPeriodBudgetColumns.map(column => {
                return {
                    label: column.label,
                    columnIndex: column.columnOrder,
                };
            });

        const allColumnsWithTotal = [
            ...months,
            ...years,
            ...trailingYearToDateCols,
            ...budgetYearToDateCols,
            ...customPeriodTrailingCols,
            ...customPeriodBudgetCols,
            ...(lastColumnForTotal
                ? [
                      {
                          label: lastColumnForTotal.label.toLowerCase(),
                          columnIndex: lastColumnForTotal.columnOrder,
                      },
                  ]
                : []),
        ];
        return {
            ...allColumnsWithTotal.reduce(
                (
                    acc: Record<string, number>,
                    {
                        label,
                        columnIndex,
                    }: { label: string; columnIndex: number },
                ) => {
                    const column = apiResponse.columns.find(
                        col => col.columnOrder === columnIndex,
                    );
                    const columnType = column?.columnType;

                    const fieldId = getFieldIdForColumn(
                        columnType,
                        columnIndex,
                    );

                    const fieldValue = data[label];
                    const hasValueThatCanBeCastedToNumber =
                        fieldValue !== undefined &&
                        fieldValue !== null &&
                        !isNaN(Number(fieldValue)) &&
                        isFinite(Number(fieldValue));

                    if (hasValueThatCanBeCastedToNumber) {
                        const fieldValueWithoutCommasAndDotsAndSpaces =
                            fieldValue.replace(/[.,\s]/g, "");
                        const fieldValueAsInteger = parseInt(
                            fieldValueWithoutCommasAndDotsAndSpaces,
                            10,
                        );
                        acc[fieldId] = fieldValueAsInteger;
                    } else {
                        acc[fieldId] = 0;
                    }
                    return acc;
                },
                {} as Record<string, number>,
            ),
        };
    };

    const formatOverlappingTotalNetIncome = (): Record<string, number> => {
        return overlappingPeriodsColumns.reduce(
            (acc, column) => {
                const fieldId = `operating-statement-month-id-${column.columnIndex}-${column.extractionId}`;
                const fieldValue = String(column.total);
                const hasValueThatCanBeCastedToNumber =
                    fieldValue !== null &&
                    !isNaN(Number(fieldValue)) &&
                    isFinite(Number(fieldValue));

                if (hasValueThatCanBeCastedToNumber) {
                    const fieldValueWithoutCommasAndDotsAndSpaces =
                        fieldValue.replace(/[.,\s]/g, "");
                    const fieldValueAsInteger = parseInt(
                        fieldValueWithoutCommasAndDotsAndSpaces,
                        10,
                    );
                    acc[fieldId] = fieldValueAsInteger;
                } else {
                    acc[fieldId] = 0;
                }
                return acc;
            },
            {} as Record<string, number>,
        );
    };

    const standardizedOriginalNetIncomeTotalName =
        apiResponse.originalNetIncomeTotalName;

    return {
        extractionId: apiResponse.extractionId,
        nonOperatingRows,
        rows: rowsPermanentWithOperatingStatements,
        columnDefsOperatingStatements,
        standardizedTotalNetIncomeOriginal: formatTotalNetIncome(
            apiResponse.originalColumnTotalCells ?? {},
        ),
        standardizedOriginalNetIncomeTotalName,
        standardizedTotalNetIncomeAltered: {
            ...(formatTotalNetIncome(apiResponse.columnTotalCells ?? {}) ?? {}),
            ...(isDraft ? formatOverlappingTotalNetIncome() : {}),
        },
        errorRows,
        activeColumns,
        monthLabelToPeriodIdMap,
        rowsDismissed,
    };
};
